import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import Alert from '../Alert';
import ModalBaseWithHeader from './ModalBaseWithHeader';
import PlayerSearch from '../Form/PlayerSearch';
import LoadingIndicator from '../LoadingIndicator';
import { useAddPlayerToTeamMutation } from '../../api/leaguesApi';
import { getSessionPin, getDistanceLabel, getEffectiveSearchLocation } from '../../utils/playerSearch';
import { getAddPlayerWarningMessage } from '../../utils/addPlayerErrors';
import { showErrorPopup } from '../../redux/errorPopupSlice';
import { addPlayerModalPropTypes } from '../../utils/proptypes';

const UNLIMITED_DISTANCE = 0;
const DISTANCE_OPTIONS = [
    { value: 25, label: '25 mi' },
    { value: 50, label: '50 mi' },
    { value: 100, label: '100 mi' },
    { value: UNLIMITED_DISTANCE, label: 'Unlimited' },
];

const AddPlayerModal = ({ open, onClose, teamId, teamName, sessionId, sessionLocation, fallbackLocations = [] }) => {
    const dispatch = useDispatch();
    const [distance, setDistance] = useState(50);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [warningMessage, setWarningMessage] = useState('');
    const [addPlayerToTeam, { isLoading }] = useAddPlayerToTeamMutation();

    const searchLocation = getEffectiveSearchLocation(sessionLocation, ...fallbackLocations);
    const pin = getSessionPin(searchLocation);
    const hasLocation = !!pin;
    const distanceLabel = getDistanceLabel(searchLocation);

    const handleClose = () => {
        setSelectedPlayer(null);
        setWarningMessage('');
        onClose();
    };

    const handlePlayerSelected = (player) => {
        setWarningMessage('');
        setSelectedPlayer(player);
    };

    const handleDistanceChange = (event) => {
        setDistance(event.target.value);
    };

    const handleAddPlayer = async () => {
        try {
            await addPlayerToTeam({ teamId, memberId: selectedPlayer.memberId, sessionId }).unwrap();
            handleClose();
        } catch (error) {
            const warning = getAddPlayerWarningMessage(error, 'Unable to add this player to the team.');

            if (warning) {
                setWarningMessage(warning);
            } else {
                dispatch(showErrorPopup(error));
            }
        }
    };

    return (
        <ModalBaseWithHeader title={teamName ? `Add Player to ${teamName}` : 'Add Player to Team'} onClose={handleClose} open={open}>
            <DialogContent sx={{ p: 4 }} dividers>
                <FormControl sx={{ minWidth: 200, mb: 3 }} disabled={!hasLocation}>
                    <InputLabel id="add-player-distance">{distanceLabel}</InputLabel>
                    <Select value={distance} label={distanceLabel} labelId="add-player-distance" onChange={handleDistanceChange}>
                        {DISTANCE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {!hasLocation && <FormHelperText>No location set — searching everywhere</FormHelperText>}
                </FormControl>
                <PlayerSearch
                    label="Search players by name"
                    searchMembersOnly
                    showUtr
                    pin={pin}
                    distance={hasLocation && distance ? `${distance}mi` : undefined}
                    noOptionsText="No players found"
                    onPlayerSelected={handlePlayerSelected}
                />
                {selectedPlayer && (
                    <Box sx={{ mt: 2 }}>
                        <Typography>{selectedPlayer.displayName}</Typography>
                        <Typography sx={{ color: 'gray' }}>{selectedPlayer.location?.display}</Typography>
                    </Box>
                )}
                {warningMessage && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {warningMessage}
                    </Alert>
                )}
                {isLoading && <LoadingIndicator />}
            </DialogContent>
            <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button variant="dark" autoFocus onClick={handleAddPlayer} disabled={!selectedPlayer || isLoading}>
                    Add to Team
                </Button>
            </DialogActions>
        </ModalBaseWithHeader>
    );
};

AddPlayerModal.propTypes = {
    ...addPlayerModalPropTypes,
};

AddPlayerModal.defaultProps = {
    open: false,
};

export default AddPlayerModal;
